import { useMachine } from "@xstate/react";
import { assign } from "xstate";
import React, { FunctionComponent, useEffect, useMemo, useRef } from "react";
import { styled } from "@iventis/styles";
import { Content } from "@iventis/translations/content/typed-content";
import { FormikProps } from "formik";
import { ScheduleConfigEventBlockType } from "@iventis/domain-model/model/scheduleConfigEventBlockType";
import { DataField } from "@iventis/domain-model/model/dataField";
import { Hierarchy } from "@iventis/domain-model/model/hierarchy";
import { EventBlock } from "@iventis/domain-model/model/eventBlock";
import { ScheduleVersion } from "@iventis/domain-model/model/scheduleVersion";
import { VenueLocation } from "@iventis/domain-model/model/venueLocation";
import { LoadingComponent, DisabledOverlay, FormWizardTemplate, Stage, ConfirmationDialogComponent } from "@iventis/components";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { toast } from "@iventis/toasts/src/toast";

import { CreateEventHeader } from "./session-dialog";
import { SubEventValues } from "./sub-event.types";
import { AddEventBlockEvent, DeleteEventBlockEvent, UpdateEventBlockEvent } from "./schedule.machine.types";
import { getEventBlockCode } from "./event-block-helpers";
import { findDirectEventBlockDescendantsBySpecificRelativeEventRuleRelationship } from "./time-rules.helpers";
import { EventBlockPutResponse } from "./schedule-api-functions";
import { SubEventCrudOperation, SubEventFormWrapper } from "./sub-event-form-wrapper";
import { subEventDialogMachine } from "./sub-event-dialog.machine";
import { getNextScheduledItemsInitialValues } from "./sub-events.helpers";

export const SubEventDialog: FunctionComponent<{
    eventBlockType: ScheduleConfigEventBlockType;
    allDataFields: DataField[];
    session: Hierarchy<EventBlock>;
    scheduleVersion: Hierarchy<ScheduleVersion>;
    sendToScheduleMachine: (event: AddEventBlockEvent | UpdateEventBlockEvent | DeleteEventBlockEvent) => void;
    close: () => void;
    crudOperation: SubEventCrudOperation;
    existingEvent?: Hierarchy<EventBlock>;
    venueHierarchy: Hierarchy<VenueLocation>[];
    eventBlockTypes: ScheduleConfigEventBlockType[];
    scheduleDaysMap: Map<number, Date>;
    currentDate: Date;
    timezone: string;
}> = ({
    session,
    scheduleVersion,
    sendToScheduleMachine,
    close,
    crudOperation,
    eventBlockType,
    existingEvent,
    allDataFields,
    venueHierarchy,
    eventBlockTypes,
    scheduleDaysMap,
    currentDate,
    timezone,
}) => {
    const translate = useIventisTranslate();

    const formRef = useRef<FormikProps<SubEventValues>>();

    const [state, send] = useMachine(subEventDialogMachine, {
        context: { crudOperation, session: session.item, existingEventBlock: existingEvent?.item, previouslySavedEventBlockId: existingEvent?.item?.id },
        actions: {
            requestSubmit: () => formRef.current.submitForm(),
            onSubmit: assign((context, event) => {
                if (context.crudOperation === SubEventCrudOperation.CREATE) {
                    sendToScheduleMachine({ type: "ADD_EVENT_BLOCK", payload: event.data as Hierarchy<EventBlock> });
                }
                if (context.crudOperation === SubEventCrudOperation.EDIT) {
                    sendToScheduleMachine({ type: "UPDATE_EVENT_BLOCK", payload: event.data as EventBlockPutResponse });
                }
                return context;
            }),
            onDelete: (_, { data }: { data: string }) => sendToScheduleMachine({ type: "DELETE_EVENT_BLOCK", payload: data }),
        },
    });

    useEffect(() => {
        if (state.done) close();
    }, [state.done]);

    const deleteConfirmationText = useMemo(
        () => ({
            title: translate(Content.schedule.scheduled_item.delete.title),
            message: translate(Content.schedule.scheduled_item.delete.message),
            confirm: translate(Content.schedule.scheduled_item.delete.confirm),
            cancel: translate(Content.schedule.scheduled_item.delete.cancel),
        }),
        [translate]
    );

    const eventBlocksRelatingToThisEvent = useMemo(
        () =>
            state.context.crudOperation === SubEventCrudOperation.EDIT
                ? findDirectEventBlockDescendantsBySpecificRelativeEventRuleRelationship(session, existingEvent?.item?.id)
                : [],
        [state.context.crudOperation]
    );

    // WIZARD TEMPLATE PROPS
    const templateProps: Stage = useMemo(
        () => ({
            primaryButtonText: translate(Content.schedule.scheduled_item.save_and_finish),
            primaryButtonCallback: () => send("SUBMIT_REQUEST", { payload: { willAddAnother: false } }),
            secondaryButtons: [
                { buttonText: translate(Content.common.buttons.cancel), onButtonPressed: close },
                {
                    buttonText: translate(Content.schedule.scheduled_item.save_and_next),
                    buttonDisabled: !state.matches("idle.validation.valid"),
                    onButtonPressed: () => send("SUBMIT_REQUEST", { payload: { willAddAnother: true } }),
                },
            ],
            isValid: state.matches("idle.validation.valid"),
            tertiaryButtonText: crudOperation === SubEventCrudOperation.EDIT ? translate(Content.schedule.scheduled_item.delete_item) : undefined,
            tertiaryButtonDisabled: eventBlocksRelatingToThisEvent.length !== 0, // need to check here if there is a relative rule relating to this
            tertiaryButtonCallback: crudOperation === SubEventCrudOperation.EDIT ? () => send("DELETE") : undefined,
            tertiaryButtonDisabledCallback: () => toast.info({ title: translate(Content.common.alert), message: translate(Content.timerules.info.can_not_delete) }),
        }),
        [state.value]
    );
    return (
        <FormWizardTemplate
            title={
                <CreateEventHeader
                    grandParentName={scheduleVersion.item.name}
                    parentName={getEventBlockCode(session.item)}
                    title={translate(Content.schedule.scheduled_item[state.context.crudOperation])}
                />
            }
            stages={[templateProps]}
            currentStage={0}
            isDeleting={state.matches("deleting")}
        >
            <SubEventFormWrapper
                // This will reset the form by refreshing the entire state of the component
                key={`sub-event-dialog-${state.context.count}`}
                existingEvent={state.context.crudOperation === SubEventCrudOperation.CREATE ? null : existingEvent}
                formRef={formRef}
                initialValueOverrides={getNextScheduledItemsInitialValues(session, state.context.previouslySavedEventBlockId)}
                allDataFields={allDataFields}
                venueHierarchy={venueHierarchy}
                scheduleVersion={scheduleVersion}
                eventBlockTypes={eventBlockTypes}
                scheduleDaysMap={scheduleDaysMap}
                currentDate={currentDate}
                eventBlockType={eventBlockType}
                session={session}
                send={send}
                timezone={timezone}
            />
            <ConfirmationDialogComponent
                show={state.matches("idle.deleteConfirmation.open")}
                title={deleteConfirmationText.title}
                message={deleteConfirmationText.message}
                confirmText={deleteConfirmationText.confirm}
                cancelText={deleteConfirmationText.cancel}
                handleCancel={() => send("CANCEL_DELETE")}
                handleConfirm={() => send("CONFIRM_DELETE", { payload: existingEvent?.item?.id })}
            />
            {state.matches("submitting") && (
                <>
                    <DisabledOverlay />
                    <StyledLoadingComponent />
                </>
            )}
        </FormWizardTemplate>
    );
};

const StyledLoadingComponent = styled(LoadingComponent)`
    top: 0;
    left: 0;
    position: absolute;
`;
